import { useEffect, useRef, useState } from 'react';
import styles from './PrintableArtwork.module.scss';
import { ReactComponent as CloseIconPressed } from '../../../assets/images/close.svg';
import GridLoader from '../../GridLoader/GridLoader';
import Button2 from '../../Buttons/Button2/Button2';
import { PrintFormat } from '../../../ressources/interfaces/printFormat';
import { post } from '../../../fetchers/fetchers';
import { CHECKOUT_SESSION_URL } from '../../../ressources/constants/apiRoutes';
import { preventMultiClickSelection } from '../../../utils/selection';
import { useOverlay } from '../../../utils/overlays';
import CheckoutForm from './CheckoutForm';

interface PrintableArtworkProps {
  displayModal: boolean;
  hideModal: Function;
  title: string | null;
  artworkURL: string | null;
  format: PrintFormat | null;
  isLoading: boolean;
  error?: string | null;
  checkoutEnabled?: boolean;
}

// en-US marks Canadian dollars as CA$, which matters to the American customers seeing them
const formatPrice = (cents: number, currency: string) =>
  new Intl.NumberFormat('en-US', {style: 'currency', currency}).format(cents / 100);

// Mirrors build_printable in the PrintableArtwork lambda: inches become percentages of the poster,
// so the preview has the same layout as the print-ready file at any display size
const PosterPreview = ({artworkURL, format} : {artworkURL: string, format: PrintFormat}) => {
  const signatureMask = `url(${format.signature}) center / contain no-repeat`;
  return (
    <div className={styles.Poster} data-testid="PosterPreview"
      style={{aspectRatio: `${format.width} / ${format.height}`, backgroundColor: format.backgroundColor}}>
      <img className={styles.PosterArtwork} src={artworkURL} alt="Artwork"
        style={{width: `${format.widthRatio * 100}%`}}/>
      <div className={styles.PosterSignature}
        style={{
          width: `${format.signatureWidth / format.width * 100}%`,
          aspectRatio: `${format.signatureAspectRatio}`,
          right: `${format.signatureMargin / format.width * 100}%`,
          bottom: `${format.signatureMargin / format.height * 100}%`,
          backgroundColor: format.primaryColor,
          mask: signatureMask,
          WebkitMask: signatureMask,
        }}/>
    </div>
  );
};

type Payment = {isStarting: boolean, clientSecret: string | null, error: string | null};
const NO_PAYMENT: Payment = {isStarting: false, clientSecret: null, error: null};

const PrintableArtwork = (props : PrintableArtworkProps) => {
  const [step, setStep] = useState<'details' | 'checkout' | 'paid'>('details');
  const [payment, setPayment] = useState<Payment>(NO_PAYMENT);
  const paymentRequestId = useRef(0);
  // So a paid artwork reopens on its confirmation rather than a new checkout
  const paidArtworks = useRef<Set<string>>(new Set());
  useOverlay(props.displayModal);
  const checkoutRef = useRef<HTMLDivElement>(null);
  const scrollIdleTimer = useRef<ReturnType<typeof setTimeout>>();

  // Shown only while scrolling, like the page's scrollbar; toggled on the element so scrolling doesn't re-render
  const showScrollbar = () => {
    checkoutRef.current?.classList.remove(styles.ScrollIdle);
    clearTimeout(scrollIdleTimer.current);
    scrollIdleTimer.current = setTimeout(() => checkoutRef.current?.classList.add(styles.ScrollIdle), 1000);
  };
  useEffect(() => () => clearTimeout(scrollIdleTimer.current), []);

  // On open: the confirmation if this artwork was paid, else the details; clear the opening click's selection
  useEffect(() => {
    if (props.displayModal) {
      // A payment still starting from the last opening no longer updates the popup
      paymentRequestId.current++;
      setPayment(NO_PAYMENT);
      setStep(props.artworkURL && paidArtworks.current.has(props.artworkURL) ? 'paid' : 'details');
      window.getSelection()?.removeAllRanges();
    }
  }, [props.displayModal, props.artworkURL]);

  const startCheckout = async () => {
    const requestId = ++paymentRequestId.current;
    setPayment({...NO_PAYMENT, isStarting: true});
    try {
      const response = await post<{client_secret: string}>(CHECKOUT_SESSION_URL, {artwork_url: props.artworkURL});
      if (requestId !== paymentRequestId.current) return;
      setPayment({...NO_PAYMENT, clientSecret: response.client_secret});
      setStep('checkout');
    } catch (error: any) {
      console.error('Failed to start the checkout:', error);
      if (requestId !== paymentRequestId.current) return;
      setPayment({...NO_PAYMENT, error: error?.response?.data?.error ?? 'The checkout could not be started; please try again'});
    }
  };

  const confirmPaid = () => {
    if (props.artworkURL) paidArtworks.current.add(props.artworkURL);
    setStep('paid');
  };

  const price = props.format?.price ?? null;
  const currency = props.format?.currency ?? 'CAD';

  let isInnerClick : boolean = false;

  return props.displayModal? (
  <div onClick={() => {if(isInnerClick){isInnerClick = false}else{props.hideModal()}}} onMouseDown={preventMultiClickSelection} className={styles.PrintableArtwork} data-testid="PrintableArtwork">
    <div onClick={() => {isInnerClick = true}} className={styles.PrintableArtworkContainer}>
      <div className={styles.Header}>
        <button className={styles.CloseButton} onClick={() => {props.hideModal()}}><CloseIconPressed className={styles.SVGClose}/></button>
      </div>
      <div className={styles.Content}>
        <div className={styles.Preview}>
          {props.isLoading && <GridLoader />}
          {!props.isLoading && props.error && <p className={styles.Error}>{props.error}</p>}
          {!props.isLoading && !props.error && props.artworkURL && props.format && <PosterPreview artworkURL={props.artworkURL} format={props.format}/>}
        </div>
        <div ref={checkoutRef} className={`${styles.Checkout} ${styles.ScrollIdle}`} data-testid="Checkout"
          onScroll={showScrollbar}>
          {step === 'details' && (<>
            <div className={styles.Details}>
              <h2 className={styles.Title}>{props.title}</h2>
              {props.format && <p className={styles.Dimensions}>{props.format.width} × {props.format.height} in</p>}
              {price !== null && <p className={styles.Price}>{formatPrice(price, currency)}</p>}
              {props.checkoutEnabled
                ? <p className={styles.Notice}>Free shipping worldwide, except to the United Kingdom.</p>
                : <p className={styles.Notice}>The website is currently under construction. Buying artwork will be available soon.</p>}
            </div>
            {payment.error && <p className={styles.Error}>{payment.error}</p>}
            {props.checkoutEnabled && (
              <Button2 action={startCheckout} disabled={price === null || payment.isStarting}>
                {payment.isStarting ? 'Starting…' : 'Buy'}
              </Button2>
            )}
          </>)}
          {step === 'checkout' && payment.clientSecret && (
            <CheckoutForm clientSecret={payment.clientSecret} onPaid={confirmPaid} onBack={() => setStep('details')}/>
          )}
          {step === 'paid' && (<>
            <div className={styles.Details}>
              <h2 className={styles.Title}>Thank you</h2>
              <p className={styles.Notice}>Your payment for {props.title} went through.</p>
            </div>
            <div className={styles.Buttons}>
              <Button2 action={() => props.hideModal()}>Close</Button2>
            </div>
          </>)}
        </div>
      </div>
    </div>
  </div> ) : null
};

export default PrintableArtwork;
